import { BaseComponent } from '../core/BaseComponent.js';
import { eventBus } from '../core/EventBus.js';
import { apiClient } from '../utils/ApiClient.js';

/**
 * TableManager - Manages data table with sorting, filtering, search and pagination
 */
export class TableManager extends BaseComponent {
    constructor(selector, options = {}) {
        super(selector, options);
        
        this.params = {
            page: 1,
            per_page: options.perPage || 10,
            search: '',
            filters: {},
            sortBy: '',
            sortDir: 'asc',
            status: options.initialStatus !== undefined ? options.initialStatus : '',
        };

        this.apiAction = options.apiAction || 'docuswift_portal_main_table';
        this.onDataLoaded = typeof options.onDataLoaded === 'function' ? options.onDataLoaded : null;
        this.searchInputSelector = options.searchInputSelector || 'input.search';
        this.tableBodySelector = options.tableBodySelector || '.main_table tbody';
        this.sortableHeadersSelector = options.sortableHeadersSelector || '.sortable';
        this.eventNamespace = options.eventNamespace || 'default';
    }

    ensureRequestsLoader() {
        const wrapper = this.element?.closest('.table_wrapper, .requests_table_wrapper') || this.element?.parentElement;
        if (!wrapper) {
            return null;
        }

        let loader = wrapper.querySelector('.ds-requests-loader');
        if (!loader) {
            wrapper.classList.add('ds-requests-wrapper');
            loader = document.createElement('div');
            loader.className = 'ds-requests-loader';
            loader.setAttribute('aria-hidden', 'true');
            loader.innerHTML = `
                <div class="ds-requests-loader__inner" role="status" aria-live="polite">
                    <span class="ds-requests-loader__spinner" aria-hidden="true"></span>
                    <span class="ds-requests-loader__text">Loading...</span>
                </div>
            `;
            wrapper.appendChild(loader);
        }

        return { wrapper, loader };
    }

    updateLoaderOffset(loader) {
        if (!loader) {
            return;
        }
        const sidebar = document.querySelector('.dashboard-wrapper #header');
        let offset = 0;
        if (sidebar) {
            const styles = window.getComputedStyle(sidebar);
            if (styles.display !== 'none' && styles.visibility !== 'hidden' && sidebar.offsetWidth > 0) {
                offset = sidebar.getBoundingClientRect().width / 2;
            }
        }
        loader.style.setProperty('--ds-loader-left-offset', `${Math.round(offset)}px`);
    }

    init() {
        if (!super.init()) return this;
        
        this.cacheElements();
        this.setupEventListeners();
        this.loadData();
        
        return this;
    }

    cacheElements() {
        // Try selecting input inside main element, fallback to global search input
        this.searchInput = document.querySelector(this.searchInputSelector);
        this.tableBody = document.querySelector(this.tableBodySelector);
        this.sortableHeaders = document.querySelectorAll(this.sortableHeadersSelector);
    }

    setupEventListeners() {
        // Search input
        console.log(this.searchInput);
        if (this.searchInput) {
            this.searchInput.addEventListener('input', (e) => {
                this.params.search = e.target.value;
                this.params.page = 1; // Reset to first page on search
                this.loadData();
            });
        }

        // Sortable columns
        this.sortableHeaders.forEach(header => {
            header.addEventListener('click', (e) => this.handleSort(e));
        });

        // Listen for pagination changes
        eventBus.on(`pagination:pageChange:${this.eventNamespace}`, (data) => {
            this.params.page = data.page;
            this.loadData();
        });

        // Details box toggle
        document.addEventListener('click', (e) => {
            const detailsLink = e.target.closest('.details_box_link');
            if (detailsLink) {
                e.preventDefault();
                this.toggleDetailsBox(detailsLink);
            }
        });

        // Facility directory: department expand rows (delegation; panel found by id from data-dept-panel-id — no inline style).
        if (this.eventNamespace === 'facilities') {
            this._onFacilitiesTableDocumentClick = (e) => this.handleFacilitiesDepartmentsToggle(e);
            document.addEventListener('click', this._onFacilitiesTableDocumentClick);
        }
    }

    /**
     * Toggle the department detail row. Uses #facility-dept-panel-{id} — not nextElementSibling (AJAX/DOM-safe).
     */
    handleFacilitiesDepartmentsToggle(e) {
        const toggle = e.target.closest('.facility-departments-toggle');
        if (!toggle) {
            return;
        }

        const table = toggle.closest('table.facilities_table');
        if (!table) {
            return;
        }

        const panelId = toggle.getAttribute('data-dept-panel-id') || toggle.getAttribute('aria-controls');
        if (!panelId) {
            return;
        }

        const expandRow = document.getElementById(panelId);
        if (!expandRow || !expandRow.classList.contains('facility-dept-expand-row')) {
            return;
        }

        e.preventDefault();

        const tbody = expandRow.closest('tbody');
        const wasCollapsed =
            expandRow.classList.contains('facility-dept-expand-row--collapsed') ||
            expandRow.hasAttribute('hidden');

        if (tbody) {
            tbody.querySelectorAll('tr.facility-dept-expand-row').forEach((tr) => {
                tr.classList.add('facility-dept-expand-row--collapsed');
                tr.setAttribute('hidden', '');
            });
            tbody.querySelectorAll('.facility-departments-toggle').forEach((a) => {
                a.setAttribute('aria-expanded', 'false');
            });
        }

        if (wasCollapsed) {
            expandRow.classList.remove('facility-dept-expand-row--collapsed');
            expandRow.removeAttribute('hidden');
            toggle.setAttribute('aria-expanded', 'true');
        }
    }

    /**
     * Handle column sorting
     */
    handleSort(e) {
        const header = e.currentTarget;
        const sortKey = header.getAttribute('data-sort');
        const currentDir = header.getAttribute('data-sort-dir');

        // Clear other sorted columns
        this.sortableHeaders.forEach(h => {
            if (h !== header) {
                h.classList.remove('sorted');
                h.setAttribute('data-sort-dir', '');
            }
        });

        // Toggle sort direction
        if (currentDir === 'desc') {
            // Third click - remove sorting
            header.classList.remove('sorted');
            header.setAttribute('data-sort-dir', '');
            this.params.sortBy = '';
            this.params.sortDir = '';
        } else {
            // Toggle between asc and desc
            const newDir = currentDir === 'asc' ? 'desc' : 'asc';
            header.setAttribute('data-sort-dir', newDir);
            header.classList.add('sorted');
            this.params.sortBy = sortKey;
            this.params.sortDir = newDir;
        }

        this.loadData();
    }

    /**
     * Toggle details box for a row
     */
    toggleDetailsBox(link) {
        const formId = link.getAttribute('data-form-id');
        const detailsBox = document.querySelector(`.details_box[data-form-id="${formId}"]`);
        
        if (detailsBox) {
            detailsBox.classList.toggle('active');
        }
    }

    /**
     * Load table data from API
     */
    async loadData() {
        this.setLoading(true);
        this.scrollToTop();

        try {
            const response = await apiClient.post(this.apiAction, {
                params: this.params
            });

            if (response.success) {
                this.updateTable(response.data.html);
                this.updatePagination(response.data.count_posts);
                if (this.onDataLoaded) {
                    this.onDataLoaded(response.data, this);
                }
            } else {
                console.error('Table load failed:', response);
            }
        } catch (error) {
            console.error('Error loading table data:', error);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Update table HTML
     */
    updateTable(html) {
        // console.log(html);
        if (this.tableBody) {
            this.tableBody.innerHTML = html;
        }
    }

    /**
     * Notify pagination component to update
     */
    updatePagination(totalPosts) {
        eventBus.emit(`pagination:update:${this.eventNamespace}`, {
            totalPosts: totalPosts,
            currentPage: this.params.page
        });
    }

    /**
     * Set loading state
     */
    setLoading(isLoading) {
        if (this.element) {
            this.element.classList.toggle('loading', isLoading);
        }
        const context = this.ensureRequestsLoader();
        if (!context) {
            return;
        }
        const { wrapper, loader } = context;
        this.updateLoaderOffset(loader);
        wrapper.classList.toggle('is-loading', isLoading);
        loader.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
    }

    /**
     * Scroll to top of page
     */
    scrollToTop() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Update filters
     */
    setFilters(filters) {
        this.params.filters = { ...filters };
        this.params.page = 1;
        this.loadData();
    }

    /**
     * Reset all filters and sorting
     */
    reset() {
        this.params = {
            page: 1,
            per_page: this.params.per_page,
            search: '',
            filters: {},
            sortBy: '',
            sortDir: 'asc'
        };

        if (this.searchInput) {
            this.searchInput.value = '';
        }

        this.sortableHeaders.forEach(h => {
            h.classList.remove('sorted');
            h.setAttribute('data-sort-dir', '');
        });

        this.loadData();
    }
}

